import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { GraduationCap, Users, Save, RefreshCw, ExternalLink, Calendar, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { getParishPage, updateParishPage } from '../../../services/api';
import RichTextEditor from '../../../components/RichTextEditor';

const ParishPagesManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('trosundervisning');
  
  const [trosundervisningContent, setTrosundervisningContent] = useState('');
  const [ungdomarContent, setUngdomarContent] = useState('');
  
  // Ungdomar meeting dates
  const [termName, setTermName] = useState('Vår-terminen 2026');
  const [meetingDates, setMeetingDates] = useState(['']);
  const [newDate, setNewDate] = useState('');

  // Trosundervisning dates
  const [trosTermName, setTrosTermName] = useState('vår-terminen 2026');
  const [karlskronaDates, setKarlskronaDates] = useState([]);
  const [karlskronaTime, setKarlskronaTime] = useState('kl. 10.00 till 12.00');
  const [olofstromDates, setOlofstromDates] = useState([]);
  const [olofstromTime, setOlofstromTime] = useState('kl. 10.00 till 12.30');
  const [vuxenDates, setVuxenDates] = useState([]);
  const [vuxenTime, setVuxenTime] = useState('Varannan tisdag, kl. 19.00');
  const [vuxenTerm, setVuxenTerm] = useState('Våren 2026');
  
  // New date inputs for each section
  const [newKarlskronaDate, setNewKarlskronaDate] = useState('');
  const [newOlofstromDate, setNewOlofstromDate] = useState('');
  const [newVuxenDate, setNewVuxenDate] = useState('');

  const fetchContent = useCallback(async () => {
    setLoading(true);
    try {
      const [trosData, ungData] = await Promise.all([
        getParishPage('trosundervisning').catch(() => null),
        getParishPage('ungdomar').catch(() => null)
      ]);
      
      if (trosData) {
        setTrosundervisningContent(trosData.content || '');
        // Load trosundervisning dates
        if (trosData.tros_term_name) setTrosTermName(trosData.tros_term_name);
        if (trosData.karlskrona_dates) setKarlskronaDates(trosData.karlskrona_dates);
        if (trosData.karlskrona_time) setKarlskronaTime(trosData.karlskrona_time);
        if (trosData.olofstrom_dates) setOlofstromDates(trosData.olofstrom_dates);
        if (trosData.olofstrom_time) setOlofstromTime(trosData.olofstrom_time);
        if (trosData.vuxen_dates) setVuxenDates(trosData.vuxen_dates);
        if (trosData.vuxen_time) setVuxenTime(trosData.vuxen_time);
        if (trosData.vuxen_term) setVuxenTerm(trosData.vuxen_term);
      }
      if (ungData) {
        setUngdomarContent(ungData.content || '');
        // Load meeting dates if available
        if (ungData.term_name) {
          setTermName(ungData.term_name);
        }
        if (ungData.meeting_dates && ungData.meeting_dates.length > 0) {
          setMeetingDates(ungData.meeting_dates);
        }
      }
    } catch (error) {
      console.error('Error fetching parish pages:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  // Add a new meeting date
  const addMeetingDate = () => {
    if (newDate.trim()) {
      setMeetingDates([...meetingDates, newDate.trim()]);
      setNewDate('');
    }
  };

  // Remove a meeting date
  const removeMeetingDate = (index) => {
    setMeetingDates(meetingDates.filter((_, i) => i !== index));
  };

  const handleSave = async (pageType) => {
    setSaving(true);
    try {
      const content = pageType === 'trosundervisning' ? trosundervisningContent : ungdomarContent;
      
      const payload = {
        content,
        updated_at: new Date().toISOString()
      };
      
      // Add ungdomar-specific fields
      if (pageType === 'ungdomar') {
        payload.term_name = termName;
        payload.meeting_dates = meetingDates.filter(d => d.trim() !== '');
      }
      
      // Add trosundervisning-specific fields
      if (pageType === 'trosundervisning') {
        payload.tros_term_name = trosTermName;
        payload.karlskrona_dates = karlskronaDates.filter(d => d.trim() !== '');
        payload.karlskrona_time = karlskronaTime;
        payload.olofstrom_dates = olofstromDates.filter(d => d.trim() !== '');
        payload.olofstrom_time = olofstromTime;
        payload.vuxen_dates = vuxenDates.filter(d => d.trim() !== '');
        payload.vuxen_time = vuxenTime;
        payload.vuxen_term = vuxenTerm;
      }
      
      await updateParishPage(pageType, payload);
      
      toast.success(`${pageType === 'trosundervisning' ? 'Trosundervisning' : 'Ungdomar'} sparad!`);
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Kunde inte spara. Försök igen.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Församlingssidor</h2>
          <p className="text-gray-600">Hantera innehållet för Trosundervisning och Ungdomar</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="trosundervisning" className="flex items-center gap-2">
            <GraduationCap className="w-4 h-4" />
            Trosundervisning
          </TabsTrigger>
          <TabsTrigger value="ungdomar" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Ungdomar
          </TabsTrigger>
        </TabsList>

        <TabsContent value="trosundervisning" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-green-600" />
                Trosundervisning
              </CardTitle>
              <p className="text-sm text-gray-500">
                Hantera datum för trosundervisning i Karlskrona, Olofström och vuxenundervisning.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* Term Name */}
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Terminens namn (visas i rubrik)
                </label>
                <Input
                  value={trosTermName}
                  onChange={(e) => setTrosTermName(e.target.value)}
                  placeholder="t.ex. vår-terminen 2026"
                  className="max-w-xs"
                />
              </div>

              {/* Karlskrona Section */}
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-yellow-600" />
                  <h3 className="font-medium text-yellow-900">Karlskrona - Datum</h3>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tid</label>
                  <Input
                    value={karlskronaTime}
                    onChange={(e) => setKarlskronaTime(e.target.value)}
                    placeholder="t.ex. kl. 10.00 till 12.00"
                    className="max-w-xs"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Aktuella datum</label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {karlskronaDates.map((date, idx) => (
                      date.trim() && (
                        <div key={`karlskrona-${date.trim()}-${idx}`} className="inline-flex items-center gap-1 bg-white border border-yellow-300 rounded-lg px-3 py-1.5">
                          <span className="text-sm font-medium text-yellow-700">{date}</span>
                          <button
                            onClick={() => setKarlskronaDates(karlskronaDates.filter((_, i) => i !== idx))}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )
                    ))}
                    {karlskronaDates.filter(d => d.trim()).length === 0 && (
                      <p className="text-sm text-gray-500 italic">Inga datum tillagda ännu</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      value={newKarlskronaDate}
                      onChange={(e) => setNewKarlskronaDate(e.target.value)}
                      placeholder="t.ex. 10 januari"
                      className="max-w-[200px]"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newKarlskronaDate.trim()) {
                          e.preventDefault();
                          setKarlskronaDates([...karlskronaDates, newKarlskronaDate.trim()]);
                          setNewKarlskronaDate('');
                        }
                      }}
                    />
                    <Button
                      type="button"
                      onClick={() => {
                        if (newKarlskronaDate.trim()) {
                          setKarlskronaDates([...karlskronaDates, newKarlskronaDate.trim()]);
                          setNewKarlskronaDate('');
                        }
                      }}
                      variant="outline"
                      size="sm"
                      className="border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Lägg till
                    </Button>
                  </div>
                </div>
              </div>

              {/* Olofström Section */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <h3 className="font-medium text-blue-900">Olofström - Datum</h3>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tid</label>
                  <Input
                    value={olofstromTime}
                    onChange={(e) => setOlofstromTime(e.target.value)}
                    placeholder="t.ex. kl. 10.00 till 12.30"
                    className="max-w-xs"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Aktuella datum</label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {olofstromDates.map((date, idx) => (
                      date.trim() && (
                        <div key={`olofstrom-${date.trim()}-${idx}`} className="inline-flex items-center gap-1 bg-white border border-blue-300 rounded-lg px-3 py-1.5">
                          <span className="text-sm font-medium text-blue-700">{date}</span>
                          <button
                            onClick={() => setOlofstromDates(olofstromDates.filter((_, i) => i !== idx))}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )
                    ))}
                    {olofstromDates.filter(d => d.trim()).length === 0 && (
                      <p className="text-sm text-gray-500 italic">Inga datum tillagda ännu</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      value={newOlofstromDate}
                      onChange={(e) => setNewOlofstromDate(e.target.value)}
                      placeholder="t.ex. 10 januari"
                      className="max-w-[200px]"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newOlofstromDate.trim()) {
                          e.preventDefault();
                          setOlofstromDates([...olofstromDates, newOlofstromDate.trim()]);
                          setNewOlofstromDate('');
                        }
                      }}
                    />
                    <Button
                      type="button"
                      onClick={() => {
                        if (newOlofstromDate.trim()) {
                          setOlofstromDates([...olofstromDates, newOlofstromDate.trim()]);
                          setNewOlofstromDate('');
                        }
                      }}
                      variant="outline"
                      size="sm"
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Lägg till
                    </Button>
                  </div>
                </div>
              </div>

              {/* Vuxenundervisning Section */}
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-purple-600" />
                  <h3 className="font-medium text-purple-900">Konvertit- och vuxenundervisning</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Termin</label>
                    <Input
                      value={vuxenTerm}
                      onChange={(e) => setVuxenTerm(e.target.value)}
                      placeholder="t.ex. Våren 2026"
                      className="max-w-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tid</label>
                    <Input
                      value={vuxenTime}
                      onChange={(e) => setVuxenTime(e.target.value)}
                      placeholder="t.ex. Varannan tisdag, kl. 19.00"
                      className="max-w-xs"
                    />
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Aktuella datum</label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {vuxenDates.map((date, idx) => (
                      date.trim() && (
                        <div key={`vuxen-${date.trim()}-${idx}`} className="inline-flex items-center gap-1 bg-white border border-purple-300 rounded-lg px-3 py-1.5">
                          <span className="text-sm font-medium text-purple-700">{date}</span>
                          <button
                            onClick={() => setVuxenDates(vuxenDates.filter((_, i) => i !== idx))}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )
                    ))}
                    {vuxenDates.filter(d => d.trim()).length === 0 && (
                      <p className="text-sm text-gray-500 italic">Inga datum tillagda ännu</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      value={newVuxenDate}
                      onChange={(e) => setNewVuxenDate(e.target.value)}
                      placeholder="t.ex. 14 januari"
                      className="max-w-[200px]"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newVuxenDate.trim()) {
                          e.preventDefault();
                          setVuxenDates([...vuxenDates, newVuxenDate.trim()]);
                          setNewVuxenDate('');
                        }
                      }}
                    />
                    <Button
                      type="button"
                      onClick={() => {
                        if (newVuxenDate.trim()) {
                          setVuxenDates([...vuxenDates, newVuxenDate.trim()]);
                          setNewVuxenDate('');
                        }
                      }}
                      variant="outline"
                      size="sm"
                      className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Lägg till
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Additional Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ytterligare innehåll (valfritt)
                </label>
                <RichTextEditor
                  value={trosundervisningContent}
                  onChange={setTrosundervisningContent}
                  placeholder="Lägg till extra information, meddelanden eller uppdateringar här..."
                />
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <a
                  href="/forsamling/trosundervisning"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center gap-1 text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  Förhandsgranska sidan
                </a>
                <Button 
                  onClick={() => handleSave('trosundervisning')}
                  disabled={saving}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Sparar...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Spara Trosundervisning
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ungdomar" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-600" />
                Ungdomar (JKUF)
              </CardTitle>
              <p className="text-sm text-gray-500">
                Redigera innehållet som visas på Ungdomar-sidan. Du kan kopiera och klistra in från Word.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Meeting Dates Section */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <h3 className="font-medium text-blue-900">Mötesdatum</h3>
                </div>
                
                {/* Term Name */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Terminens namn
                  </label>
                  <Input
                    value={termName}
                    onChange={(e) => setTermName(e.target.value)}
                    placeholder="t.ex. Vår-terminen 2026"
                    className="max-w-xs"
                  />
                </div>
                
                {/* Current Meeting Dates */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Aktuella datum (visas på sidan)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {meetingDates.map((date, idx) => (
                      date.trim() && (
                        <div 
                          key={`meeting-${date}-${idx}`}
                          className="inline-flex items-center gap-1 bg-white border border-blue-300 rounded-lg px-3 py-1.5"
                        >
                          <span className="text-sm font-medium text-blue-700">{date}</span>
                          <button
                            onClick={() => removeMeetingDate(idx)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            title="Ta bort datum"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )
                    ))}
                    {meetingDates.filter(d => d.trim()).length === 0 && (
                      <p className="text-sm text-gray-500 italic">Inga datum tillagda ännu</p>
                    )}
                  </div>
                </div>
                
                {/* Add New Date */}
                <div className="flex items-center gap-2">
                  <Input
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                    placeholder="Lägg till datum (t.ex. 10/04)"
                    className="max-w-[200px]"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addMeetingDate())}
                  />
                  <Button
                    type="button"
                    onClick={addMeetingDate}
                    variant="outline"
                    size="sm"
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Lägg till
                  </Button>
                </div>
              </div>
              
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm text-orange-800">
                  <strong>Tips:</strong> Sidan visar automatiskt information om JKUF och mötesdatum ovan. 
                  Använd redigeraren nedan för att lägga till extra information eller uppdateringar.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ytterligare innehåll (valfritt)
                </label>
                <RichTextEditor
                  value={ungdomarContent}
                  onChange={setUngdomarContent}
                  placeholder="Lägg till extra information, meddelanden eller uppdateringar här..."
                />
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <a
                  href="/forsamling/ungdomar"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center gap-1 text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  Förhandsgranska sidan
                </a>
                <Button 
                  onClick={() => handleSave('ungdomar')}
                  disabled={saving}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Sparar...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Spara Ungdomar
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ParishPagesManager;
